import { 
  createBrowserRouter, 
  Navigate,
} from "react-router-dom";
import { Suspense, lazy } from "react";
import * as Sentry from "@sentry/react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Dashboard from "./pages/Dashboard";
import ClientsPage from "./pages/ClientsPage";
import ClientProfilePage from "./pages/ClientProfilePage";
import BookingsPage from "./pages/BookingsPage";
import VenuesPage from "./pages/VenuesPage";
import InvoicesPage from "./pages/InvoicesPage";
import Index from "./pages/Index";
import AuthCallback from "./pages/AuthCallback";
import OnboardingPage from "./pages/OnboardingPage";
import ProfilePage from "./pages/ProfilePage";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";

const MealBreakHelpSheetAuditPage = lazy(() => import("./pages/MealBreakHelpSheetAuditPage"));
const ShortTurnHelpSheetAuditPage = lazy(() => import("./pages/ShortTurnHelpSheetAuditPage"));
const HolidayAdminPage = lazy(() => import("./pages/HolidayAdminPage"));
const RateLadderOffsetGraphicAuditPage = lazy(() => import("./pages/audit/RateLadderOffsetGraphicAudit"));
const CountModeOffsetGraphicAuditPage = lazy(() => import("./pages/audit/CountModeOffsetGraphicAudit"));

// Wrap createBrowserRouter with Sentry instrumentation
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
  {
    path: "/",
    element: <Index />,
    errorElement: <ErrorBoundary />,
  },
  { 
    path: "/dashboard", 
    element: <ProtectedRoute><Dashboard /></ProtectedRoute>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/clients",
    element: <ProtectedRoute><ClientsPage /></ProtectedRoute>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/clients/:clientId",
    element: <ProtectedRoute><ClientProfilePage /></ProtectedRoute>,
    errorElement: <ErrorBoundary />,
  },
  { 
    path: "/bookings", 
    element: <ProtectedRoute><BookingsPage /></ProtectedRoute>,
    errorElement: <ErrorBoundary />,
  },
  { 
    path: "/user_venues", 
    element: <ProtectedRoute><VenuesPage /></ProtectedRoute>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/invoices",
    element: <ProtectedRoute><InvoicesPage /></ProtectedRoute>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/profile/*",
    element: <ProtectedRoute><ProfilePage /></ProtectedRoute>,
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <ProfilePage /> },
      { path: "contract", element: <ProfilePage /> },
      { path: "invoicing", element: <ProfilePage /> },
    ]
  },
  {
    path: "/auth/callback",
    element: <AuthCallback />,
    errorElement: <ErrorBoundary />,
  },
  ...(import.meta.env.DEV
    ? [{
        path: "/dev/meal-break-help-sheet-audit",
        element: (
          <Suspense fallback={null}>
            <MealBreakHelpSheetAuditPage />
          </Suspense>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: "/dev/short-turn-help-sheet-audit",
        element: (
          <Suspense fallback={null}>
            <ShortTurnHelpSheetAuditPage />
          </Suspense>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: "/dev/holiday-admin",
        element: (
          <Suspense fallback={null}>
            <HolidayAdminPage />
          </Suspense>
        ),
        errorElement: <ErrorBoundary />,
      }]
    : []),
  {
    path: "/onboarding",
    element: <OnboardingPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/audit/rate-ladder-offset-graphic",
    element: (
      <Suspense fallback={null}>
        <RateLadderOffsetGraphicAuditPage />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/audit/count-mode-offset-graphic",
    element: (
      <Suspense fallback={null}>
        <CountModeOffsetGraphicAuditPage />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
    errorElement: <ErrorBoundary />
  }
], {
  future: {
    v7_relativeSplatPath: true,
    v7_normalizeFormMethod: true
  }
});
